import { useQuery } from 'react-apollo'
import gql from 'graphql-tag'
import { hasPermission } from 'src/services'
import useGroupId from './useGroupId'
import useCurrentUserView from './useCurrentUserView'
import useChannelId from './useChannelId'

const groupPermissionsQuery = gql`
  query groupPermissions($groupId: ID!) {
    group(id: $groupId) {
      id
      permissions {
        update {
          affectedChannels
          alarmsReporting
          groupRoles
          information
          relays
          plan
        }
      }
    }
  }
`

const channelPermissionsQuery = gql`
  query channelPermissions($channelId: ID!) {
    channel(id: $channelId) {
      id
      permissions {
        update {
          configuration
          acknowledge
          computeMkt
          note
          immediateMeasurement
        }
        print {
          view
          sterilization
          alarms
        }
      }
    }
  }
`

export const appPermissionsQuery = gql`
  query appPermissions {
    permissions {
      account {
        update {
          accessLevel
          dutyNumbers
          groupRoles
          information
          jobRoles
          password
          preferences
          status
        }
      }
      alarms {
        read
        print
      }
      auditTrails {
        read
        print
      }
      channels {
        create
        read
        delete
        print
        importEscFile
      }
      duties {
        read
        update {
          account
          users
        }
      }
      groups {
        create
        read
        delete
        print
        toggle
      }
      ackComments {
        create
        delete
      }
      systemSettings {
        read
        update {
          cfr21
          ldap
          systemAlarms
          rightAssertions
          services
          smtp
          preferences
        }
      }
      systemAlarms {
        read
        print
      }
      users {
        create
        read
        update {
          accessLevel
          dutyNumbers
          groupRoles
          information
          jobRoles
          password
          preferences
          status
        }
        delete
        print
      }
    }
  }
`

export function useAppPermission(path) {
  const { loading, data: { permissions } = {} } = useQuery(appPermissionsQuery)

  if (loading) {
    return false
  } else {
    return hasPermission(permissions, path)
  }
}

export function useGroupPermission(groupId, path) {
  const { loading, data: { group: { permissions } = {} } = {} } = useQuery(groupPermissionsQuery, {
    variables: { groupId },
    skip: groupId === undefined,
  })

  if (loading || !groupId) {
    return false
  } else {
    return hasPermission(permissions, path)
  }
}

export function useChannelPermission(channelId, path) {
  const { loading, data: { channel: { permissions } = {} } = {} } = useQuery(channelPermissionsQuery, {
    variables: { channelId },
    skip: channelId === undefined,
  })

  if (loading || !channelId) {
    return false
  } else {
    return hasPermission(permissions, path)
  }
}

export function useCurrentChannelPermission(path) {
  const channelId = useChannelId()
  const channelPermission = useChannelPermission(channelId, path)
  return !channelId || channelPermission
}

export function useCurrentGroupPermission(path) {
  const groupId = useGroupId()
  const groupPermission = useGroupPermission(groupId, path)
  return !groupId || groupPermission
}

export function useUserUpdatePermission(path) {
  const hasAccountPermission = useAppPermission(`account.update.${path}`)
  const hasUserPermission = useAppPermission(`users.update.${path}`)
  return useCurrentUserView().isAccountView ? hasAccountPermission : hasUserPermission
}
